/**
 * Calculates contract pricing discounts for products.
 *
 * Contract pricing allows employers to provide discounts to their employees,
 * configured per product category or per product. This mirrors the server's
 * ContractPricing::DiscountCalculator.effective_discount resolution: a
 * per-product entry overrides its category's rate, and an explicit 'none'
 * entry means no discount at all (no category fallback), so client-side
 * display always matches what the Cart charges.
 *
 * @param {Object} product - The product to check pricing for
 * @param {Object} contractPricingData - Contract pricing configuration from server
 * @param {Object} contractPricingData.discounts - Discount configuration by category
 * @param {Object} contractPricingData.products - Per-product discount terms by sku
 * @param {Object} contractPricingData.category_map - Product-to-pricing category map
 * @returns {Object} - Contract pricing calculation result
 */

export default function getContractPricing(product, contractPricingData) {
  const originalPrice = product?.price ? parseFloat(product.price) : 0
  const categoryMap = contractPricingData?.category_map || {}
  const discounts = contractPricingData?.discounts || {}
  const productEntries = contractPricingData?.products || {}

  const noDiscount = {
    isDiscounted: false,
    discountedPrice: null,
    originalPrice,
    discountAmount: 0,
    discountPercentage: null,
  }

  if (!product) {
    return noDiscount
  }

  const productEntry = product.sku ? productEntries[product.sku] : null
  if (productEntry?.type === 'none') {
    return noDiscount
  }

  const discount = productEntry || discounts[categoryMap[product.category]]
  if (!discount) {
    return noDiscount
  }

  const safeAmount = Math.max(discount.amount, 0)
  const discountAmount = discount.type === 'percentage'
    ? originalPrice * (safeAmount / 100)
    : Math.min(safeAmount, originalPrice)

  const discountedPrice = Math.max(originalPrice - discountAmount, 0)

  return {
    isDiscounted: discountAmount > 0,
    discountedPrice,
    originalPrice,
    discountAmount,
    discountPercentage: discount.type === 'percentage' ? safeAmount : null,
  }
}
